import chroma from "chroma-js";
import { NamedColorPalette, ColorScale } from "../types";
import { GlobalColor, GlobalColorGroup } from "@/types/colors";
import { WebsiteColorPalettes } from "@/subapps/onboarding/types";
import { createVariableName } from "@/lib/colors/colors";
import { documentStore } from "@/store/store";
import { findNode } from "@/lib/tree/tree";
import lodash from "lodash";

export const generatePalette = (
  baseColor: string,
  primary = false,
  locked = false
): NamedColorPalette => {
  const scale = {} as ColorScale;

  const base = chroma(baseColor).hex();
  const label = getClosestColorName(base);

  scale[500] = createGlobalColor(base, label, "500");

  scale[100] = createGlobalColor(
    chroma.mix(baseColor, "#ffffff", 0.9).saturate(-1).hex(),
    label,
    "100"
  );
  scale[200] = createGlobalColor(
    chroma.mix(baseColor, "#ffffff", 0.75).saturate(-0.8).hex(),
    label,
    "200"
  );
  scale[300] = createGlobalColor(
    chroma.mix(baseColor, "#ffffff", 0.6).saturate(-0.5).hex(),
    label,
    "300"
  );

  scale[400] = createGlobalColor(
    chroma(baseColor).brighten(0.6).hex(),
    label,
    "400"
  );
  scale[600] = createGlobalColor(
    chroma(baseColor).darken(0.6).hex(),
    label,
    "600"
  );

  scale[700] = createGlobalColor(
    chroma(baseColor).darken(1.2).hex(),
    label,
    "700"
  );
  scale[800] = createGlobalColor(
    chroma(baseColor).darken(1.8).saturate(-0.3).hex(),
    label,
    "800"
  );
  scale[900] = createGlobalColor(
    chroma(baseColor).darken(4.0).saturate(-0.2).hex(),
    label,
    "900"
  );

  const text = isLightColor(baseColor) ? scale[900] : scale[100];

  return {
    label,
    primary,
    locked,
    base: scale[500],
    scale,
    text
  };
};

export function createGlobalColor(
  hex: string,
  label: string,
  scale: string
): GlobalColor {
  return {
    cssVariableName: createVariableName(`onboarding-${label}-${scale}`),
    label: `${label} ${scale}`,
    value: hex
  };
}

export const websiteColorPalettesToGlobalColorsArray = (
  websitePalettes: WebsiteColorPalettes
): GlobalColor[] => {
  const globalColors: GlobalColor[] = [];

  // Add primary palette's scale to globalColors
  if (websitePalettes.primary) {
    globalColors.push(...Object.values(websitePalettes.primary.scale));
  }

  // Add neutral palette's scale to globalColors
  if (websitePalettes.neutral) {
    globalColors.push(...Object.values(websitePalettes.neutral.scale));
  }

  // Add secondary palettes' scales to globalColors
  if (websitePalettes.secondary && Array.isArray(websitePalettes.secondary)) {
    websitePalettes.secondary.forEach(palette => {
      globalColors.push(...Object.values(palette.scale));
    });
  }

  return globalColors;
};

export const primaryToBreakdanceBrandGlobalColor = (
  primaryPalette: NamedColorPalette
): GlobalColor => {
  return {
    cssVariableName: createVariableName(`brand-color`),
    label: `Brand Color`,
    value: primaryPalette.base.value
  };
};

export const isDarkColor = (color?: string | null): boolean => {
  if (!color) return false;
  return chroma(color).luminance() < 0.5;
};

export const isLightColor = (color?: string | null): boolean => {
  if (!color) return false;
  return chroma(color).luminance() > 0.5;
};

export const getClosestColorName = (hex: string): string => {
  const inputLab = chroma(hex).lab();

  let closestName = "";
  let minDistance = Infinity;

  for (const { name, hex: refHex } of BASIC_COLOR_NAMES) {
    const distance = chroma.deltaE(
      chroma.lab(...inputLab),
      chroma.lab(...chroma(refHex).lab())
    );
    if (distance < minDistance) {
      minDistance = distance;
      closestName = name;
    }
  }

  return closestName;
};

export const randomColor = (): string => {
  // return chroma.random().hex();

  const randomIndex = Math.floor(Math.random() * BASIC_COLOR_NAMES.length);
  return BASIC_COLOR_NAMES[randomIndex].hex;
};

export function inlineContainerStyle(
  color: string,
  palette: NamedColorPalette
) {
  return {
    backgroundColor: color,
    color: isLightColor(color)
      ? String(palette.scale[900].value)
      : String(palette.scale[100].value)
  };
}

export const isDarkOrLight = (color: string): "is-dark" | "is-light" => {
  return chroma(color).luminance() < 0.5 ? "is-dark" : "is-light";
};

export function groupedGlobalColors(
  globalColors: GlobalColor[]
): GlobalColorGroup[] {
  const groupMap = new Map();

  globalColors.forEach(item => {
    const match = item.cssVariableName.match(
      /bde-palette-onboarding-([a-z-]+)-\d+/
    );
    if (match) {
      const groupName = match[1];
      if (groupName === "neutral") {
        return; // Skip the "neutral" palette
      }
      if (!groupMap.has(groupName)) {
        groupMap.set(groupName, []);
      }
      groupMap.get(groupName).push(item);
    }
  });

  return Array.from(groupMap.values()).map(group => {
    return {
      base: group[5],
      dark: {
        background: group[8],
        text: group[0]
      },
      light: {
        background: group[0],
        text: group[8]
      },
      brand: {
        background: group[4],
        background_hover: group[5],
        text: isLightColor(group[4].value.toString()) ? group[8] : group[0]
      }
    };
  });
}

export function isSectionUsingDarkBackgroundColor(
  elementId: number,
  colorGroup: GlobalColorGroup
): boolean {
  const element = findNode(documentStore.document.tree, elementId);
  if (!element) return false;

  const backgroundColor = lodash.get(
    element.data.properties,
    "design.background.color"
  );

  return (
    backgroundColor === `var(--${colorGroup.dark.background.cssVariableName})`
  );
}

export async function toggleSectionColors(
  elementId: number,
  colorGroup: GlobalColorGroup
): Promise<boolean> {
  const isUsingDark = isSectionUsingDarkBackgroundColor(elementId, colorGroup);

  const backgroundColor = isUsingDark
    ? colorGroup.light.background.cssVariableName
    : colorGroup.dark.background.cssVariableName;
  const textColor = isUsingDark
    ? colorGroup.light.text.cssVariableName
    : colorGroup.dark.text.cssVariableName;

  // Update the design properties
  documentStore.unthrottledPropertyChanged({
    elementId,
    path: "design",
    meta: {
      snapshotLabel: `Updated background color`
    },
    value: {
      background: {
        color: `var(--${backgroundColor})`
      },
      text_colors: {
        headings: `var(--${textColor})`,
        text: `var(--${textColor})`,
        brand: `var(--${colorGroup.brand.background.cssVariableName})`,
        links: `var(--${colorGroup.brand.background.cssVariableName})`
      },
      buttons: {
        primary: {
          background: `var(--${colorGroup.brand.background.cssVariableName})`,
          background_hover: `var(--${colorGroup.brand.background_hover.cssVariableName})`,
          typography: {
            color: `var(--${colorGroup.brand.text.cssVariableName})`
          }
        }
        // secondary: {
        //   outline: "true",
        //   color: `var(--${newVal.colorPalettes.neutral?.scale[300].cssVariableName})`,
        //   typography: {
        //     color: `var(--${newVal.colorPalettes.neutral?.scale[900].cssVariableName})`
        //   }
        // }
      }
    }
  });

  // Toggle the dark mode
  return !isUsingDark;
}

export const BASIC_COLOR_NAMES = [
  { name: "White", hex: "#ffffff" },
  { name: "Snow", hex: "#fffafa" },
  { name: "Honeydew", hex: "#f0fff0" },
  { name: "Mint Cream", hex: "#f5fffa" },
  { name: "Azure", hex: "#f0ffff" },
  { name: "Alice Blue", hex: "#f0f8ff" },
  { name: "Ghost White", hex: "#f8f8ff" },
  { name: "White Smoke", hex: "#f5f5f5" },
  { name: "Seashell", hex: "#fff5ee" },
  { name: "Beige", hex: "#f5f5dc" },
  { name: "Old Lace", hex: "#fdf5e6" },
  { name: "Floral White", hex: "#fffaf0" },
  { name: "Ivory", hex: "#fffff0" },
  { name: "Antique White", hex: "#faebd7" },
  { name: "Linen", hex: "#faf0e6" },
  { name: "Lavender Blush", hex: "#fff0f5" },
  { name: "Misty Rose", hex: "#ffe4e1" },
  { name: "Gainsboro", hex: "#dcdcdc" },
  { name: "Light Gray", hex: "#d3d3d3" },
  { name: "Silver", hex: "#c0c0c0" },
  { name: "Dark Gray", hex: "#a9a9a9" },
  { name: "Gray", hex: "#808080" },
  { name: "Dim Gray", hex: "#696969" },
  { name: "Light Slate Gray", hex: "#778899" },
  { name: "Slate Gray", hex: "#708090" },
  { name: "Dark Slate Gray", hex: "#2f4f4f" },
  { name: "Black", hex: "#000000" },
  { name: "Lavender", hex: "#e6e6fa" },
  { name: "Thistle", hex: "#d8bfd8" },
  { name: "Plum", hex: "#dda0dd" },
  { name: "Violet", hex: "#ee82ee" },
  { name: "Orchid", hex: "#da70d6" },
  { name: "Fuchsia", hex: "#ff00ff" },
  { name: "Magenta", hex: "#ff00ff" },
  { name: "Medium Orchid", hex: "#ba55d3" },
  { name: "Medium Purple", hex: "#9370db" },
  { name: "Blue Violet", hex: "#8a2be2" },
  { name: "Dark Violet", hex: "#9400d3" },
  { name: "Dark Orchid", hex: "#9932cc" },
  { name: "Dark Magenta", hex: "#8b008b" },
  { name: "Purple", hex: "#800080" },
  { name: "Indigo", hex: "#4b0082" },
  { name: "Medium Slate Blue", hex: "#7b68ee" },
  { name: "Slate Blue", hex: "#6a5acd" },
  { name: "Dark Slate Blue", hex: "#483d8b" },
  { name: "Rebecca Purple", hex: "#663399" },
  { name: "Medium Blue", hex: "#0000cd" },
  { name: "Blue", hex: "#0000ff" },
  { name: "Royal Blue", hex: "#4169e1" },
  { name: "Dodger Blue", hex: "#1e90ff" },
  { name: "Deep Sky Blue", hex: "#00bfff" },
  { name: "Sky Blue", hex: "#87ceeb" },
  { name: "Light Sky Blue", hex: "#87cefa" },
  { name: "Steel Blue", hex: "#4682b4" },
  { name: "Light Steel Blue", hex: "#b0c4de" },
  { name: "Light Blue", hex: "#add8e6" },
  { name: "Powder Blue", hex: "#b0e0e6" },
  { name: "Pale Turquoise", hex: "#afeeee" },
  { name: "Dark Turquoise", hex: "#00ced1" },
  { name: "Medium Turquoise", hex: "#48d1cc" },
  { name: "Turquoise", hex: "#40e0d0" },
  { name: "Aqua", hex: "#00ffff" },
  { name: "Cyan", hex: "#00ffff" },
  { name: "Light Cyan", hex: "#e0ffff" },
  { name: "Cadet Blue", hex: "#5f9ea0" },
  { name: "Medium Aquamarine", hex: "#66cdaa" },
  { name: "Aquamarine", hex: "#7fffd4" },
  { name: "Dark Green", hex: "#006400" },
  { name: "Dark Olive Green", hex: "#556b2f" },
  { name: "Dark Sea Green", hex: "#8fbc8f" },
  { name: "Sea Green", hex: "#2e8b57" },
  { name: "Medium Sea Green", hex: "#3cb371" },
  { name: "Light Sea Green", hex: "#20b2aa" },
  { name: "Pale Green", hex: "#98fb98" },
  { name: "Spring Green", hex: "#00ff7f" },
  { name: "Lawn Green", hex: "#7cfc00" },
  { name: "Chartreuse", hex: "#7fff00" },
  { name: "Medium Spring Green", hex: "#00fa9a" },
  { name: "Green Yellow", hex: "#adff2f" },
  { name: "Lime Green", hex: "#32cd32" },
  { name: "Yellow Green", hex: "#9acd32" },
  { name: "Forest Green", hex: "#228b22" },
  { name: "Olive Drab", hex: "#6b8e23" },
  { name: "Dark Khaki", hex: "#bdb76b" },
  { name: "Khaki", hex: "#f0e68c" },
  { name: "Pale Goldenrod", hex: "#eee8aa" },
  { name: "Light Goldenrod Yellow", hex: "#fafad2" },
  { name: "Light Yellow", hex: "#ffffe0" },
  { name: "Yellow", hex: "#ffff00" },
  { name: "Gold", hex: "#ffd700" },
  { name: "Light Goldenrod", hex: "#eedd82" },
  { name: "Goldenrod", hex: "#daa520" },
  { name: "Dark Goldenrod", hex: "#b8860b" },
  { name: "Rosy Brown", hex: "#bc8f8f" },
  { name: "Indian Red", hex: "#cd5c5c" },
  { name: "Saddle Brown", hex: "#8b4513" },
  { name: "Sienna", hex: "#a0522d" },
  { name: "Peru", hex: "#cd853f" },
  { name: "Burlywood", hex: "#deb887" },
  { name: "Beige", hex: "#f5f5dc" },
  { name: "Wheat", hex: "#f5deb3" },
  { name: "Sandy Brown", hex: "#f4a460" },
  { name: "Tan", hex: "#d2b48c" },
  { name: "Chocolate", hex: "#d2691e" },
  { name: "Firebrick", hex: "#b22222" },
  { name: "Brown", hex: "#a52a2a" },
  { name: "Dark Salmon", hex: "#e9967a" },
  { name: "Salmon", hex: "#fa8072" },
  { name: "Light Salmon", hex: "#ffa07a" },
  { name: "Orange", hex: "#ffa500" },
  { name: "Dark Orange", hex: "#ff8c00" },
  { name: "Coral", hex: "#ff7f50" },
  { name: "Tomato", hex: "#ff6347" },
  { name: "Orange Red", hex: "#ff4500" },
  { name: "Red", hex: "#ff0000" },
  { name: "Crimson", hex: "#dc143c" },
  { name: "Deep Pink", hex: "#ff1493" },
  { name: "Hot Pink", hex: "#ff69b4" },
  { name: "Light Pink", hex: "#ffb6c1" },
  { name: "Pink", hex: "#ffc0cb" },
  { name: "Pale Violet Red", hex: "#db7093" },
  { name: "Medium Violet Red", hex: "#c71585" },
  { name: "Orchid", hex: "#da70d6" },
  { name: "Medium Orchid", hex: "#ba55d3" },
  { name: "Dark Orchid", hex: "#9932cc" },
  { name: "Dark Violet", hex: "#9400d3" },
  { name: "Blue Violet", hex: "#8a2be2" },
  { name: "Medium Purple", hex: "#9370db" },
  { name: "Slate Blue", hex: "#6a5acd" },
  { name: "Dark Slate Blue", hex: "#483d8b" },
  { name: "Blush Pink", hex: "#ff6fff" },
  { name: "Sunset Orange", hex: "#ff4500" },
  { name: "Ocean Blue", hex: "#0077be" },
  { name: "Forest Green", hex: "#228b22" },
  { name: "Lemon Yellow", hex: "#fff44f" },
  { name: "Peach Puff", hex: "#ffdab9" },
  { name: "Coral Reef", hex: "#ff7f50" },
  { name: "Mint Green", hex: "#98ff98" },
  { name: "Skyline Blue", hex: "#87ceeb" },
  { name: "Amber Glow", hex: "#ffbf00" },
  { name: "Ruby Red", hex: "#9b111e" },
  { name: "Emerald Green", hex: "#50c878" },
  { name: "Sapphire Blue", hex: "#0f52ba" },
  { name: "Goldenrod Yellow", hex: "#daa520" },
  { name: "Cinnamon Brown", hex: "#d2691e" },
  { name: "Lilac Mist", hex: "#c8a2c8" },
  { name: "Teal Wave", hex: "#008080" },
  { name: "Periwinkle Blue", hex: "#ccccff" },
  { name: "Rosewood", hex: "#65000b" },
  { name: "Sunflower Yellow", hex: "#ffda03" },
  { name: "Bubblegum Pink", hex: "#ffc1cc" },
  { name: "Electric Purple", hex: "#bf00ff" },
  { name: "Cobalt Blue", hex: "#0047ab" },
  { name: "Chartreuse Green", hex: "#7fff00" },
  { name: "Tangerine", hex: "#f28500" },
  { name: "Mango Tango", hex: "#ff8243" },
  { name: "Turquoise Blue", hex: "#40e0d0" },
  { name: "Crimson Tide", hex: "#dc143c" },
  { name: "Platinum Gray", hex: "#e5e4e2" },
  { name: "Ivory White", hex: "#fffff0" },
  { name: "Denim Blue", hex: "#1560bd" },
  { name: "Blazing Red", hex: "#ff2400" },
  { name: "Pine Green", hex: "#01796f" },
  { name: "Carmine Pink", hex: "#eb4c42" },
  { name: "Lavender Dream", hex: "#e6e6fa" },
  { name: "Midnight Blue", hex: "#191970" },
  { name: "Frost White", hex: "#f8f8ff" },
  { name: "Copper Penny", hex: "#ad6f69" },
  { name: "Sand Dune", hex: "#967117" },
  { name: "Arctic Blue", hex: "#d0fefe" },
  { name: "Blazing Yellow", hex: "#ffcc00" },
  { name: "Rose Quartz", hex: "#aa98a9" },
  { name: "Moss Green", hex: "#8a9a5b" },
  { name: "Berry Purple", hex: "#8a2be2" },
  { name: "Citrus Orange", hex: "#f4a460" },
  { name: "Powder Pink", hex: "#ffb6c1" },
  { name: "Steel Gray", hex: "#71797e" },
  { name: "Neon Green", hex: "#39ff14" },
  { name: "Velvet Red", hex: "#7e354d" },
  { name: "Aqua Marine", hex: "#7fffd4" },
  { name: "Golden Glow", hex: "#f9a602" },
  { name: "Cherry Blossom", hex: "#ffb7c5" },
  { name: "Deep Sea Blue", hex: "#001f3f" },
  { name: "Cranberry Red", hex: "#db143c" },
  { name: "Olive Green", hex: "#808000" },
  { name: "Rust Orange", hex: "#b7410e" },
  { name: "Peacock Blue", hex: "#1c39bb" },
  { name: "Sunset Pink", hex: "#ffcccb" },
  { name: "Lime Punch", hex: "#dfff00" },
  { name: "Eggplant Purple", hex: "#614051" },
  { name: "Cobalt Teal", hex: "#007ba7" },
  { name: "Amber Sunset", hex: "#ff4500" },
  { name: "Frosted Mint", hex: "#dbfff8" },
  { name: "Cedar Brown", hex: "#4b3621" },
  { name: "Ocean Mist", hex: "#b0e0e6" },
  { name: "Golden Peach", hex: "#ffcba4" },
  { name: "Rosewood Red", hex: "#65000b" },
  { name: "Cactus Green", hex: "#587058" },
  { name: "Tropical Blue", hex: "#007fff" },
  { name: "Blush Rose", hex: "#ff6f91" },
  { name: "Lava Red", hex: "#cf1020" },
  { name: "Pale Peach", hex: "#ffe5b4" },
  { name: "Azure Sky", hex: "#007fff" },
  { name: "Cinnamon Spice", hex: "#d2691e" },
  { name: "Goldenrod", hex: "#daa520" },
  { name: "Fuchsia Glow", hex: "#ff77ff" },
  { name: "Deep Plum", hex: "#8b008b" },
  { name: "Seafoam Green", hex: "#9fe2bf" },
  { name: "Citrus Lime", hex: "#bfff00" },
  { name: "Berry Red", hex: "#b22222" },
  { name: "Tangerine Dream", hex: "#ffcc00" },
  { name: "Electric Blue", hex: "#7df9ff" },
  { name: "Coral Pink", hex: "#f88379" },
  { name: "Lemon Chiffon", hex: "#fffacd" },
  { name: "Pale Lavender", hex: "#dcd0ff" },
  { name: "Bright Cyan", hex: "#00ffff" },
  { name: "Burnt Sienna", hex: "#e97451" },
  { name: "Golden Champagne", hex: "#f7e7ce" },
  { name: "Rose Pink", hex: "#ff66cc" },
  { name: "Deep Magenta", hex: "#cc00cc" },
  { name: "Pale Aqua", hex: "#bcd4e6" },
  { name: "Sunset Gold", hex: "#ffcc33" },
  { name: "Bright Maroon", hex: "#c32148" },
  { name: "Soft Peach", hex: "#ffe5b4" }
];
